import React from 'react';
import { CheckCircle2, Download, TrendingDown, TrendingUp } from 'lucide-react';

export interface OptimizationSnapshotMetrics {
  leadTimeMinutes: number;
  pcePercent: number;
  nvaCost: number;
  deliveryCapacityPerDay: number;
}

export interface OptimizationSummaryData {
  before: OptimizationSnapshotMetrics;
  after: OptimizationSnapshotMetrics;
  estimatedRoiPercent: number;
  netGain: number;
  investment: number;
  generatedAt: string;
  executionMs: number;
}

interface OptimizationSummaryCardProps {
  data: OptimizationSummaryData;
  onGenerateReport?: () => void;
  onApplyMap?: () => void;
}

const formatMinutesToDuration = (minutes: number) => {
  const safe = Math.max(0, Math.round(minutes));
  const hours = Math.floor(safe / 60);
  const mins = safe % 60;
  if (hours <= 0) return `${mins}m`;
  return `${hours}h ${mins.toString().padStart(2, '0')}m`;
};

const formatPercent = (value: number) => `${value.toFixed(1)}%`;

const formatCurrency = (value: number) =>
  value.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    maximumFractionDigits: 0,
  });

const deltaClass = (isPositive: boolean) =>
  isPositive ? 'text-emerald-600' : 'text-red-600';

const DeltaCell: React.FC<{
  positiveWhen: boolean;
  text: string;
}> = ({ positiveWhen, text }) => (
  <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${deltaClass(positiveWhen)}`}>
    {positiveWhen ? <TrendingDown className="h-3 w-3" /> : <TrendingUp className="h-3 w-3" />}
    {text}
  </span>
);

export const OptimizationSummaryCard: React.FC<OptimizationSummaryCardProps> = ({
  data,
  onGenerateReport,
  onApplyMap,
}) => {
  const leadDelta = data.before.leadTimeMinutes - data.after.leadTimeMinutes;
  const pceDelta = data.after.pcePercent - data.before.pcePercent;
  const nvaDelta = data.before.nvaCost - data.after.nvaCost;
  const capDelta = data.after.deliveryCapacityPerDay - data.before.deliveryCapacityPerDay;
  const netRoi = data.netGain - data.investment;
  const reductionPercent =
    data.before.leadTimeMinutes > 0
      ? ((data.before.leadTimeMinutes - data.after.leadTimeMinutes) / data.before.leadTimeMinutes) * 100
      : 0;

  return (
    <section className="relative border border-slate-300 bg-white">
      <div className="grid grid-cols-[1fr_auto] border-b border-slate-200 bg-slate-50/70 px-3 py-2">
        <div>
          <h3 className="text-[11px] font-semibold uppercase tracking-wider text-slate-700">Relatorio de Auditoria de Fluxo</h3>
          <p className="text-[10px] uppercase tracking-wide text-slate-500">Comparativo tecnico do estado atual versus otimizado</p>
        </div>
        <div className="text-right text-[10px] text-slate-500">
          <div className="font-mono tabular-nums">{data.executionMs.toFixed(0)}ms</div>
          <div className="font-mono tabular-nums">{new Date(data.generatedAt).toLocaleString('pt-BR')}</div>
        </div>
      </div>

      <div className="overflow-hidden">
        <table className="w-full border-collapse text-[11px]">
          <thead className="bg-slate-100 text-slate-600 uppercase tracking-wider">
            <tr>
              <th className="border-r border-slate-200 px-2 py-1 text-left font-semibold">Metrica</th>
              <th className="border-r border-slate-200 px-2 py-1 text-right font-semibold">Estado Atual</th>
              <th className="border-r border-slate-200 px-2 py-1 text-right font-semibold">Estado Otimizado</th>
              <th className="px-2 py-1 text-right font-semibold">Impacto (Delta)</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 font-medium text-slate-700">Lead Time Total</td>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatMinutesToDuration(data.before.leadTimeMinutes)}</td>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatMinutesToDuration(data.after.leadTimeMinutes)}</td>
              <td className="border-t border-slate-200 px-2 py-1.5 text-right">
                <DeltaCell positiveWhen={leadDelta > 0} text={`${formatMinutesToDuration(Math.abs(leadDelta))}`} />
              </td>
            </tr>
            <tr>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 font-medium text-slate-700">Eficiencia de Ciclo (PCE)</td>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatPercent(data.before.pcePercent)}</td>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatPercent(data.after.pcePercent)}</td>
              <td className="border-t border-slate-200 px-2 py-1.5 text-right">
                <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${deltaClass(pceDelta >= 0)}`}>
                  {pceDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {formatPercent(Math.abs(pceDelta))}
                </span>
              </td>
            </tr>
            <tr>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 font-medium text-slate-700">Custo NVA</td>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatCurrency(data.before.nvaCost)}</td>
              <td className="border-r border-t border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{formatCurrency(data.after.nvaCost)}</td>
              <td className="border-t border-slate-200 px-2 py-1.5 text-right">
                <DeltaCell positiveWhen={nvaDelta > 0} text={formatCurrency(Math.abs(nvaDelta))} />
              </td>
            </tr>
            <tr>
              <td className="border-r border-y border-slate-200 px-2 py-1.5 font-medium text-slate-700">Capacidade de Entrega</td>
              <td className="border-r border-y border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{Math.round(data.before.deliveryCapacityPerDay)}/dia</td>
              <td className="border-r border-y border-slate-200 px-2 py-1.5 text-right font-mono tabular-nums text-slate-800">{Math.round(data.after.deliveryCapacityPerDay)}/dia</td>
              <td className="border-y border-slate-200 px-2 py-1.5 text-right">
                <span className={`inline-flex items-center gap-1 font-mono tabular-nums font-semibold ${deltaClass(capDelta >= 0)}`}>
                  {capDelta >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                  {Math.abs(Math.round(capDelta))}/dia
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div className="border-t-2 border-slate-800 bg-slate-900 px-3 py-2 text-white">
        <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-300">Resumo de Ganhos (ROI Real)</div>
        <div className="grid grid-cols-4 gap-2 text-[11px]">
          <div className="border border-slate-700 bg-slate-950/40 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">ROI Estimado</div>
            <div className="font-mono tabular-nums font-semibold">{formatPercent(data.estimatedRoiPercent)}</div>
          </div>
          <div className="border border-slate-700 bg-slate-950/40 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Ganho Bruto</div>
            <div className="font-mono tabular-nums font-semibold text-emerald-300">{formatCurrency(data.netGain)}</div>
          </div>
          <div className="border border-slate-700 bg-slate-950/40 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Investimento</div>
            <div className="font-mono tabular-nums font-semibold text-amber-200">{formatCurrency(data.investment)}</div>
          </div>
          <div className="border border-slate-700 bg-slate-950/40 px-2 py-1">
            <div className="text-[10px] uppercase tracking-wide text-slate-400">Resultado Liquido</div>
            <div className={`font-mono tabular-nums font-semibold ${netRoi >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>{formatCurrency(netRoi)}</div>
          </div>
        </div>
        <div className="mt-1 text-[10px] text-slate-300">
          Lead Time reduzido em <span className="font-mono tabular-nums font-semibold">{formatPercent(Math.max(0, reductionPercent))}</span> com auditoria validada via IA.
        </div>
      </div>

      <div className="flex items-center justify-end gap-2 border-t border-slate-200 bg-white px-3 py-2">
        <button
          type="button"
          onClick={onGenerateReport}
          className="inline-flex h-7 items-center gap-1 border border-slate-300 bg-white px-2 text-[11px] font-semibold text-slate-700 hover:bg-slate-50"
        >
          <Download className="h-3.5 w-3.5" />
          Gerar Relatorio Executivo
        </button>
        <button
          type="button"
          onClick={onApplyMap}
          className="inline-flex h-7 items-center gap-1 border border-blue-700 bg-blue-700 px-2 text-[11px] font-semibold text-white hover:bg-blue-800"
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          Aplicar no Mapa
        </button>
      </div>

      <div className="pointer-events-none absolute left-2 top-2 text-[9px] font-semibold uppercase tracking-[0.16em] text-slate-300/70">
        Simulacao validada via IA
      </div>
    </section>
  );
};

export default OptimizationSummaryCard;
